import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { PERMISSIONS } from "@/constants/permissions"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import useUser from "@/hooks/useUser"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useEffect, useState } from "react"
import { catalog, type Company } from "./api"

export function DiscoveryReviewPanel({ company }: { company: Company }) {
  const { user, hasPermission } = useUser()
  const { translate: t } = useTranslation()
  const client = useQueryClient()
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 60000)
    return () => window.clearInterval(timer)
  }, [])
  const key = [
    "company-index",
    user?.organization,
    "discovery-review",
    company.id,
  ]
  const review = useQuery({
    queryKey: key,
    queryFn: () => catalog.discoveryReview(company.id),
    refetchInterval: 60000,
  })
  const [editing, setEditing] = useState(false)
  const [url, setUrl] = useState(company.website)
  const [notes, setNotes] = useState("")
  const [confirmed, setConfirmed] = useState(false)
  const save = useMutation({
    mutationFn: () =>
      catalog.recordDiscoveryReview(company.id, {
        url,
        notes: notes.trim(),
        reviewed: true,
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: key })
      setEditing(false)
      setConfirmed(false)
      setNotes("")
    },
  })
  return (
    <section className="space-y-3 rounded-xl border border-border p-5">
      <h2 className="font-semibold">{t("CI_DISCOVERY_REVIEW")}</h2>
      <p className="text-sm text-muted-foreground">
        {t("CI_DISCOVERY_REVIEW_HINT")}
      </p>
      {review.isPending && <p>{t("CI_LOADING")}</p>}
      {review.isError && (
        <p role="alert">
          {t("CI_ERROR")}{" "}
          <Button onClick={() => void review.refetch()}>{t("CI_RETRY")}</Button>
        </p>
      )}
      {review.isSuccess &&
        (review.data ? (
          <div className="space-y-2 text-sm">
            <p>
              {t("CI_LAST_DISCOVERY_REVIEW")}:{" "}
              {new Date(review.data.createdAt).toLocaleString()} ·{" "}
              {review.data.actor}
            </p>
            <a
              className="break-all underline"
              href={review.data.url}
              target="_blank"
              rel="noreferrer"
            >
              {review.data.url}
            </a>
            <p className="whitespace-pre-wrap">{review.data.notes}</p>
            <p>
              {t(
                now >= new Date(review.data.nextDue).getTime()
                  ? "CI_DISCOVERY_DUE"
                  : "CI_DISCOVERY_NEXT",
              )}
              : {new Date(review.data.nextDue).toLocaleDateString()}
            </p>
          </div>
        ) : (
          <p>{t("CI_DISCOVERY_UNREVIEWED")}</p>
        ))}
      {hasPermission(PERMISSIONS.MC_DECLARATIONS_UPLOAD) && (
        <Button
          variant="outline"
          aria-expanded={editing}
          onClick={() => {
            setEditing(!editing)
            save.reset()
          }}
        >
          {t("CI_RECORD_DISCOVERY")}
        </Button>
      )}
      {editing && hasPermission(PERMISSIONS.MC_DECLARATIONS_UPLOAD) && (
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault()
            if (confirmed && notes.trim()) save.mutate()
          }}
        >
          <label className="block">
            {t("CI_REVIEWED_URL")}
            <Input
              aria-label={t("CI_REVIEWED_URL")}
              type="url"
              pattern="https?://.*"
              required
              maxLength={4096}
              value={url}
              onChange={(e) => setUrl(e.target.value)}
            />
          </label>
          <label className="block">
            {t("CI_DISCOVERY_NOTES")}
            <Textarea
              aria-label={t("CI_DISCOVERY_NOTES")}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              maxLength={4000}
              required
            />
          </label>
          <label className="flex items-start gap-2">
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
            />
            {t("CI_DISCOVERY_CONFIRM")}
          </label>
          <Button
            type="submit"
            disabled={save.isPending || !confirmed || !notes.trim() || !url}
          >
            {t("CI_SAVE_DISCOVERY")}
          </Button>
        </form>
      )}
      {save.isError && <p role="alert">{t("CI_ERROR")}</p>}
      {save.isSuccess && <p role="status">{t("CI_DISCOVERY_SAVED")}</p>}
    </section>
  )
}
